// Need to use the React-specific entry point to import `createApi`
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  setCartItem,
  setCartOffers,
  setCartSummary,
  setCustomer,
  setOrder,
} from "./cartSlice";
import { setCookie } from "cookies-next/client";

interface GetCartItemParams {
  couponCode?: string;
  guestUserId: string | null;
  countryCode: string;
  cartSession?: string | null;
}
interface GetCustomerParams {
  cartId: number;
}
interface AddCartItemParams {
  couponCode?: string;
  countryCode: string;
  guestUserId?: string;
  serviceId: number;
  eventId: number;
  offerId: number | null;
  eventDetailId: number;
  quantity: number;
}

interface ChangeQuantityParams {
  cartId: number;
  quantity: number;
}

interface RemoveCartItemParams {
  cartId: number;
}
interface ApplyCouponParams {
  cartId: number;
  couponCode: string;
}

interface CartResponse {
  cartItem: CartItem;
  cartSummary: CartSummary;
  offers: CartOffer[];
}
interface CartCustomerAndOrderResponse {
  customer: Customer;
  order: Order;
}
interface AddItemCartResponse {
  message: string;
  guestId: string;
}

// Define a service using a base URL and expected endpoints
export const cartApiSlice = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.NEXT_PUBLIC_BACKEND_URL + "/api",
    prepareHeaders: (headers) => {
      headers.set("Accept", "application/json");
      return headers;
    },
  }),
  reducerPath: "cartApi",
  tagTypes: ["cart"],
  endpoints: (build) => ({
    // ✅ Get Cart Items
    getCartItem: build.query<CartResponse, GetCartItemParams>({
      query: (params) => {
        const queryString = new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null) {
                acc[key] = String(value);
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString();
        return `/get-cart-item?${queryString}`;
      },
      onQueryStarted: async (queryArgument, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCartItem(data.cartItem));
          dispatch(setCartSummary(data.cartSummary));
          dispatch(setCartOffers(data.offers));
        } catch (error) {
          console.log("error :>> ", error);
        }
      },
      providesTags: ["cart"],
    }),

    //Get Customer detail associated with cart
    getCustomerAndOrder: build.query<
      CartCustomerAndOrderResponse,
      GetCustomerParams
    >({
      query: (params) => `/get-cart-customer-and-order?cartId=${params.cartId}`,
      onQueryStarted: async (queryArgument, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCustomer(data.customer));
          dispatch(setOrder(data.order));
        } catch (error) {
          console.log("error :>> ", error);
        }
      },
    }),

    // ✅ Change Quantity Mutation
    addCartItem: build.mutation<AddItemCartResponse, AddCartItemParams>({
      query: (body) => ({
        url: "/add-cart-item",
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          setCookie("guestId", data?.guestId);
        } catch (error) {
          console.log("error adding cart item:", error);
        }
      },
      invalidatesTags: ["cart"],
    }),

    // ✅ Change Quantity Mutation
    changeQuantity: build.mutation<CartResponse, ChangeQuantityParams>({
      query: (body) => ({
        url: "/change-cart-quantity",
        method: "PUT",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          console.log("data :>> ", data);
        } catch (error) {
          console.log("error changing quantity:", error);
        }
      },
      invalidatesTags: ["cart"],
    }),

    // ✅ Remove Cart Item Mutation
    removeCartItem: build.mutation<CartResponse, RemoveCartItemParams>({
      query: (body) => ({
        url: "/remove-cart-item",
        method: "DELETE",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          console.log("data :>> ", data);
          dispatch(setCartItem({} as CartItem));
          dispatch(setCartSummary({} as CartSummary));
          dispatch(setCartOffers([]));
        } catch (error) {
          console.log("error removing cart item:", error);
        }
      },
      invalidatesTags: ["cart"],
    }),
    applyCoupon: build.mutation<CartResponse, ApplyCouponParams>({
      query: (body) => ({
        url: "/apply-coupon",
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          console.log("data :>> ", data);
        } catch (error) {
          console.log("error applying coupon:", error);
        }
      },
      invalidatesTags: ["cart"],
    }),
    removeCoupon: build.mutation<CartResponse, ApplyCouponParams>({
      query: (body) => ({
        url: "/remove-coupon",
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          console.log("data :>> ", data);
        } catch (error) {
          console.log("error removing coupon:", error);
        }
      },
      invalidatesTags: ["cart"],
    }),
  }),
});

// Hooks are auto-generated by RTK-Query
export const {
  useGetCartItemQuery,
  useGetCustomerAndOrderQuery,
  useLazyGetCustomerAndOrderQuery,
  useChangeQuantityMutation,
  useRemoveCartItemMutation,
  useAddCartItemMutation,
  useApplyCouponMutation,
  useRemoveCouponMutation,
} = cartApiSlice;
